import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowRight, Apple, Play } from "lucide-react";
import { useWaitlist } from "@/context/WaitlistContext";
import { useTestFlight } from "@/context/TestFlightContext";
import { stickyVariant } from "@/hooks/useABTest";
import { trackAB, getABConfig } from "@/lib/api";
import { GOOGLE_PLAY_URL, TESTFLIGHT_URL, isAndroid, isIOS } from "@/lib/testflight";
import { useLiveAirports } from "@/lib/useLiveAirports";

const easing = [0.22, 1, 0.36, 1];

// Canonical subhead (Oct 2026 hero brief) — same string on both A/B arms so
// the instrumentation stays live while the copy is fixed.
const SUBCOPY = {
  A: "GateMate gives every venue and event its own live chatroom. Check in when you arrive, see who's there, and connect with the people sharing your night.",
  B: "GateMate gives every venue and event its own live chatroom. Check in when you arrive, see who's there, and connect with the people sharing your night.",
};
// AVAILABILITY (Sept 2026): GateMate is live on the App Store and Google Play.
// The waitlist dialog remains as the notify/capture path; store badges link
// straight to the live listings. Android: REACT_APP_GOOGLE_PLAY_URL.
const CTA = { A: "Get notified at launch", B: "Get notified at launch" };

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, ease: easing, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { openWaitlist } = useWaitlist();
  const { testFlightReady } = useTestFlight();
  const [variants, setVariants] = useState({ sub: "A", cta: "A" });
  const [qrOpen, setQrOpen] = useState(false);
  const liveAirports = useLiveAirports();
  const ref = useRef(null);

  // Item 9: the hero loop only autoplays when it should. Reduced-motion,
  // Save-Data, slow connections (2g/3g) and phones get the poster frame
  // instead — the video element is never mounted, so nothing downloads.
  const [videoOn, setVideoOn] = useState(false);
  useEffect(() => {
    try {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const mobile = window.matchMedia("(max-width: 768px)").matches;
      const conn = navigator.connection || {};
      const slow = conn.saveData || ["slow-2g", "2g", "3g"].includes(conn.effectiveType);
      setVideoOn(!(reduced || slow || mobile));
    } catch (_) {
      setVideoOn(true);
    }
  }, []);

  // Primary CTA (Oct 2026 brief): phones go straight to their store; desktop
  // opens a small panel with a QR to /download plus both store badges.
  const primaryCtaLabel = testFlightReady ? "Get the App" : CTA[variants.cta] || CTA.A;
  const onPrimaryClick = () => {
    trackAB("hero_subcopy", variants.sub, "conversion").catch(() => {});
    trackAB("cta_copy", variants.cta, "conversion").catch(() => {});
    if (!testFlightReady) {
      openWaitlist();
      return;
    }
    if (isIOS()) {
      window.location.href = TESTFLIGHT_URL;
    } else if (isAndroid()) {
      window.location.href = GOOGLE_PLAY_URL;
    } else {
      setQrOpen(true);
    }
  };

  useEffect(() => {
    let sub = stickyVariant("hero_subcopy", ["A", "B"]);
    let cta = stickyVariant("cta_copy", ["A", "B"]);
    const finalize = (locks = {}) => {
      if (locks.hero_subcopy) sub = locks.hero_subcopy;
      if (locks.cta_copy) cta = locks.cta_copy;
      setVariants({ sub, cta });
      trackAB("hero_subcopy", sub, "impression");
      trackAB("cta_copy", cta, "impression");
    };
    getABConfig().then((cfg) => finalize(cfg.locks || {})).catch(() => finalize({}));
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, -6]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  // Muted autoplay is allowed by every modern browser, but some environments
  // still resolve the element paused — nudge it programmatically on mount.
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  }, [videoOn]);

  return (
    <section ref={ref} id="hero" className="relative min-h-screen pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      {/* ambient glows */}
      <motion.div style={{ y: glowY }} className="glow-orb w-[520px] h-[520px] bg-neon-violet/25 -top-20 -left-20" />
      <motion.div style={{ y: glowY }} className="glow-orb w-[460px] h-[460px] bg-neon-cyan/20 top-40 right-0" />

      <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 items-center min-h-[80vh]">
        {/* Copy */}
        <div className="lg:col-span-6">
          {/* Headline is the USPTO trademark specimen wording — do not change. */}
          <h1 className="font-display font-bold tracking-tighter leading-[0.88] text-6xl sm:text-7xl lg:text-[5.5rem]">
            <MaskLine delay={0.35}>
              <span className="text-white">Step In.</span>
            </MaskLine>
            <MaskLine delay={0.5}>
              <span className="text-gradient">Link Up.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-7 text-lg md:text-xl text-zinc-400 max-w-xl leading-relaxed"
          >
            {SUBCOPY[variants.sub]}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.88, duration: 0.6 }}
            className="mt-4 inline-flex items-center gap-2 text-[15px] text-zinc-500 max-w-xl leading-relaxed"
            data-testid="hero-dfw-policy"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-neon-cyan shadow-[0_0_10px_rgba(0,198,174,0.6)]" />
            <span>
              Free for attendees. Venue and event partnerships now open in Dallas–Fort Worth.
            </span>
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.92, duration: 0.6 }}
            className="mt-2 text-sm text-zinc-600"
            data-testid="hero-age-note"
          >
            18+ / 21+. ID rules follow each venue.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Button
              onClick={onPrimaryClick}
              data-testid="hero-waitlist-button"
              className="group rounded-full h-14 px-8 text-base bg-gradient-to-r from-neon-violet to-neon-cyan text-white font-semibold btn-glow border-0"
            >
              {primaryCtaLabel}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>

          {/* Returning visitors with a room code skip straight to the web app —
              framed this way so first-time strangers don't hit a sign-in wall cold. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="mt-4"
          >
            <a
              href="https://app.gatemate.net/sign-in"
              data-testid="hero-web-app-link"
              className="text-sm text-zinc-400 hover:text-neon-cyan transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-neon-cyan/50"
            >
              Have a room code? Join on the web →
            </a>
          </motion.div>

          {/* Store badges — custom-styled glass chips that match the site aesthetic.
              Both stores are live; each links straight to its listing. */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-6 flex flex-wrap items-center gap-3"
          >
            <a
              href={TESTFLIGHT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-badge-appstore"
              className="inline-flex items-center gap-2.5 rounded-xl glass hover:glass-cyan px-4 py-2.5 transition-all"
            >
              <Apple className="w-5 h-5 text-white shrink-0" />
              <span className="text-left leading-tight">
                <span className="block text-[9px] uppercase tracking-wider text-zinc-400">Download on the</span>
                <span className="block text-sm font-semibold text-white">App Store</span>
              </span>
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-badge-googleplay"
              className="inline-flex items-center gap-2.5 rounded-xl glass hover:glass-cyan px-4 py-2.5 transition-all"
            >
              <Play className="w-5 h-5 text-white shrink-0" />
              <span className="text-left leading-tight">
                <span className="block text-[9px] uppercase tracking-wider text-zinc-400">Get it on</span>
                <span className="block text-sm font-semibold text-white">Google Play</span>
              </span>
            </a>
          </motion.div>

          {/* Live proof — only rendered while airport rooms are actually open
              (data: public venue directory, polled every 45s). */}
          {liveAirports.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="mt-5"
            >
              <span
                className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium text-zinc-300"
                data-testid="hero-airport-live-badge"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-neon-cyan opacity-70 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-neon-cyan" />
                </span>
                {liveAirports.length >= 2
                  ? "Live now at DFW & Love Field airports"
                  : `Live now: ${liveAirports[0].name}`}
              </span>
            </motion.div>
          )}
        </div>

        {/* Hero phone — auto-playing, muted loop of the real story-ordered App Store
            screenshots (source of truth: /app/backend/scripts/gen_hero_screens_loop.py).
            Poster = first screenshot so LCP stays fast even before the video loads.
            Reduced-motion / Save-Data / 2g-3g visitors get the poster frame only. */}
        <div className="lg:col-span-6 relative">
          <motion.div
            style={{ y: phoneY, rotate: phoneRotate }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.7, ease: easing }}
            className="relative"
          >
            <div className="animate-float relative">
              {/* Cinematic neon backdrop plate behind the video phone
                  (AI scene plate: /app/backend/scripts/gen_perfect_phones.py) */}
              <img
                src="/generated/hero-backdrop.webp"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[540px] sm:w-[640px] lg:w-[720px] max-w-none rounded-[2.5rem] opacity-70 [mask-image:radial-gradient(72%_72%_at_50%_50%,black_55%,transparent_97%)]"
              />
              <div className="relative mx-auto w-[236px] sm:w-[272px] lg:w-[296px] rounded-[3rem] p-[8px] bg-gradient-to-b from-white/15 to-white/5 ring-1 ring-white/10 drop-shadow-[0_30px_60px_rgba(0,124,255,0.35)]">
                <div className="rounded-[2.5rem] overflow-hidden bg-black">
                  {videoOn ? (
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      poster="/generated/v3-01-hero.webp"
                      aria-label="Looping tour of real GateMate iOS app screens — join a room, open your own, chat, share and keep your nights"
                      data-testid="hero-screens-video"
                      className="w-full h-auto block"
                    >
                      {/* Slow connections (2g/3g or narrow viewports) get the
                          360px variant — much smaller, still crisp on the phone
                          mockup. Desktop/fast connections get the 480px version. */}
                      <source src="/generated/hero-screens-loop-360.mp4" type="video/mp4" media="(max-width: 640px)" />
                      <source src="/generated/hero-screens-loop.mp4?v=13" type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src="/generated/v3-01-hero.webp"
                      alt="GateMate app welcome screen — Step In. Link Up."
                      data-testid="hero-screens-poster"
                      className="w-full h-auto block"
                      fetchpriority="high"
                    />
                  )}
                </div>
                <span className="absolute top-[8px] left-1/2 -translate-x-1/2 h-[20px] w-[64px] rounded-full bg-black/95 ring-1 ring-white/10 pointer-events-none" />
                <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 h-[12px] w-[70%] rounded-full bg-neon-cyan/25 blur-md pointer-events-none" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Desktop "Get the App" panel — QR to the download page + store badges */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent
          className="bg-midnight border-white/10 max-w-sm text-center"
          data-testid="hero-get-app-panel"
        >
          <p className="font-display text-xl font-bold text-white">Get GateMate on your phone</p>
          <p className="mt-1 text-sm text-zinc-400">
            Point your camera at the code — it opens gatemate.net/download.
          </p>
          <div className="mx-auto mt-4 bg-white rounded-2xl p-3 w-48">
            <img
              src="/generated/qr-download-page.png"
              alt="QR code to gatemate.net/download"
              className="w-full h-auto block"
              data-testid="hero-get-app-qr"
            />
          </div>
          <div className="mt-5 flex items-center justify-center gap-3">
            <a
              href={TESTFLIGHT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-panel-appstore"
              className="inline-flex items-center gap-2 rounded-xl glass hover:glass-cyan px-4 py-2.5 transition-all"
            >
              <Apple className="w-5 h-5 text-white" />
              <span className="text-sm font-semibold text-white">App Store</span>
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-panel-googleplay"
              className="inline-flex items-center gap-2 rounded-xl glass hover:glass-cyan px-4 py-2.5 transition-all"
            >
              <Play className="w-5 h-5 text-white" />
              <span className="text-sm font-semibold text-white">Google Play</span>
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-neon-cyan to-transparent" />
      </motion.div>
    </section>
  );
}
